import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Award, Heart, Users, ShoppingCart, MapPin, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FiestaMedal = () => {
  const [searchParams] = useSearchParams();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    name: '',
    email: '',
    phone: '',
    pickupDate: '',
    pickupTime: 'morning',
    quantity: 1
  });
  const [submitting, setSubmitting] = useState(false);

  // Handle PayPal return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const orderId = searchParams.get('order_id');
    const token = searchParams.get('token');
    
    if (payment === 'success' && token) {
      // Capture the payment
      capturePayment(token, sessionStorage.getItem(`medal-capability-${token}`));
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled.');
    }
  }, [searchParams]);

  const capturePayment = async (paypalOrderId, capabilityToken) => {
    try {
      const response = await fetch(`${API_URL}/api/store/fiesta-medal/capture/${paypalOrderId}`, {
        method: 'POST', headers: { 'X-Payment-Capability': capabilityToken || '' }
      });
      
      if (response.ok) {
        setShowSuccessModal(true);
        // Clear URL params
        window.history.replaceState({}, '', '/about/fiesta-medal');
      } else {
        toast.error('Failed to complete payment. Please contact us.');
      }
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Something went wrong. Please contact us.');
    }
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/store/fiesta-medal/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...purchaseForm,
          amount: purchaseForm.quantity * 25
        })
      });
      
      const data = await response.json();
      
      if (data.paypal_url) {
        sessionStorage.setItem(`medal-capability-${data.paypal_order_id}`, data.capability_token);
        if (data.mocked) {
          await capturePayment(data.paypal_order_id, data.capability_token);
          return;
        }
        // Redirect to PayPal
        window.location.href = data.paypal_url;
      } else {
        toast.error('Failed to create order. Please try again.');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="fiesta-medal-page">
      <SEOHead
        title="The First Fiesta Medal | Pompeo Coppini's San Antonio Legacy"
        description="The story of how sculptor Pompeo Coppini created the first Fiesta medal in 1905, launching a beloved San Antonio tradition that continues today."
        canonicalPath="/about/fiesta-medal"
      />
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-serif text-stone-900 mb-4">Order Complete!</h3>
            <p className="text-stone-600 mb-6">
              Thank you for your purchase! A confirmation email has been sent with your pickup details.
            </p>
            <p className="text-sm text-stone-500 mb-6">
              <strong>Pickup Location:</strong><br />
              The Coppini Academy of Fine Art<br />
              115 Melrose Place<br />
              San Antonio, TX 78212
            </p>
            <Button 
              onClick={() => setShowSuccessModal(false)}
              className="bg-cafa-red hover:bg-cafa-red-900 text-white"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Hero Section with Original Medal Image */}
      <section className="relative bg-stone-900 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="inline-block px-4 py-1 bg-cafa-red/20 text-cafa-red-100 text-sm font-medium rounded-full mb-6">
                San Antonio History
              </span>
              <h1 className="text-4xl sm:text-5xl font-serif text-white mb-6 leading-tight">
                The First Fiesta Medal: Pompeo Coppini and the Birth of a San Antonio Tradition
              </h1>
              <p className="text-xl text-stone-300">
                Discover the story of how a visionary Italian sculptor created the first Fiesta medal in 1905, 
                launching a beloved San Antonio tradition that continues to this day.
              </p>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/vb6ejw4k_ratio1x1_1920.jpg"
                alt="The original 1905 Fiesta Medal designed by Pompeo Coppini"
                className="w-80 h-80 object-cover rounded-full shadow-2xl border-4 border-white/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-stone max-w-none">
            <p className="text-xl text-stone-700 leading-relaxed mb-6">
              In 2016, during a routine archival review of the Coppini Academy of Fine Arts' historical collection, 
              a remarkable discovery was made: a small, pressed-bronze medal bearing the inscription 
              "San Antonio Spring Carnival Apr 1905" and the enigmatic letters "K of O." This 2-inch artifact, 
              found at the bottom of a box alongside other treasures from sculptor Pompeo Coppini's personal effects, 
              would prove to be the earliest known Fiesta medal in existence.
            </p>
            <p className="text-lg text-stone-600 leading-relaxed">
              The medal's discovery represents more than a historical curiosity—it connects modern San Antonio 
              to its cultural roots and reveals the pivotal role that Pompeo Coppini played in shaping one of 
              the city's most cherished traditions.
            </p>
          </div>
        </div>
      </section>

      {/* Historical Image Gallery */}
      <section className="py-12 bg-stone-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/vb6ejw4k_ratio1x1_1920.jpg"
                alt="Close-up of the original 1905 Fiesta Medal"
                className="w-full rounded-lg shadow-lg"
              />
              <p className="text-sm text-stone-600 text-center italic">
                The original 1905 Fiesta Medal, featuring Coppini's distinctive multicultural design
              </p>
            </div>
            <div className="space-y-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/i7iw6nxe_1920x0.jpg"
                alt="Pompeo Coppini in his studio"
                className="w-full rounded-lg shadow-lg"
              />
              <p className="text-sm text-stone-600 text-center italic">
                Coppini in his studio
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pull Quote */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="text-center">
            <p className="text-2xl md:text-3xl font-serif text-stone-800 italic leading-relaxed">
              "The more we learn about Coppini, the more we realize he was a visionary—deeply involved 
              in every aspect of the city he loved."
            </p>
            <footer className="mt-4 text-stone-600">— Coppini Academy Archives</footer>
          </blockquote>
        </div>
      </section>

      {/* Pompeo Coppini Biography */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">Pompeo Coppini and the Birth of the Fiesta Medal</h2>
          
          <div className="prose prose-lg prose-stone max-w-none">
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Pompeo Coppini (1870–1957) arrived in San Antonio as an Italian immigrant who would become 
              one of the most influential sculptors in Texas history. His artistic legacy includes 36 public 
              monuments across the United States, most notably the 60-foot granite Alamo Cenotaph titled 
              "Spirit of Sacrifice," which stands as a solemn tribute to the defenders who fell at the Alamo in 1836.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Beyond his sculptural achievements, Coppini was a civic leader deeply committed to San Antonio's 
              cultural development. He played a crucial role in early preservation efforts for the Alamo, 
              reportedly introducing historian Adina De Zavala to businesswoman Clara Driscoll, whose funding 
              helped save the historic mission from commercial development.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              In 1905, Coppini founded the Knights of Omala—"Alamo" spelled backward—a civic organization of 
              prominent San Antonio businessmen and community leaders. Also known as the "Phunny Phellows," 
              the Knights were instrumental in transforming what had been a single-day Battle of Flowers parade 
              into the multi-day celebration we know today as Fiesta San Antonio.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              The organization eventually evolved into the Order of the Alamo, which continues to present the 
              Coronation of the Fiesta Queen to this day.
            </p>
          </div>
        </div>
      </section>

      {/* The Original Medal */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">The Original 1905 Medal</h2>
          
          <div className="prose prose-lg prose-stone max-w-none">
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              The 1905 medal features a striking design: the profile of a figure wearing stacked sombreros 
              partially covering a feather headdress, with a half-face masquerade mask. Coppini intended 
              this composite figure to represent "those who had settled and lived in San Antonio"—a tribute 
              to the city's rich multicultural heritage.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Originally, these medals were not collectible souvenirs but functional badges. They were first 
              distributed at Knights of Omala meetings, then mailed to members who had paid their subscriptions. 
              The King Selamat ("tamales" spelled backward) and Queen wore the medals during early carnival celebrations.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              By 1907, the badges began to be sold to the general public for one dollar. A San Antonio Express 
              article from March 1908 optimistically predicted that "hundreds of San Antonians will be wearing them." 
              Little did they know that more than a century later, Fiesta medals would become one of the most 
              beloved and distinctive aspects of San Antonio's annual celebration, with hundreds of organizations 
              producing their own unique designs each year.
            </p>
          </div>
        </div>
      </section>

      {/* Rediscovery */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">The Rediscovery of a Lost Artifact</h2>
          
          <div className="prose prose-lg prose-stone max-w-none">
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              For over a century, the original 1905 medal remained hidden among Coppini's personal belongings 
              in the historic studio he built in 1936 on Melrose Place—the same studio that now houses the 
              Coppini Academy of Fine Arts.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              During archival work in 2016, the medal was discovered at the bottom of a box, resting alongside 
              Coppini's Order of the Cross medal—an honor bestowed upon him when he was knighted by the King of 
              Italy in 1931 for his artistic contributions.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              The Institute of Texan Cultures, which has displayed King Antonio medals from the 1920s, verified 
              the Coppini Academy's discovery as genuine. The find was described as a "holy grail moment" for 
              San Antonio history—concrete evidence of the Fiesta medal tradition's origins and Coppini's 
              foundational role in creating it.
            </p>
          </div>
        </div>
      </section>

      {/* Pull Quote 2 */}
      <section className="py-12 bg-cafa-red">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="text-center">
            <p className="text-2xl md:text-3xl font-serif text-white italic leading-relaxed">
              "Fiesta medals are such a rich part of San Antonio culture now. To be able to see the very 
              first one is very special."
            </p>
          </blockquote>
        </div>
      </section>

      {/* From Badge to Tradition */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">From Badge to Beloved Tradition</h2>
          
          <div className="prose prose-lg prose-stone max-w-none">
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              What began as a simple membership badge for an early 20th-century civic organization has evolved 
              into one of San Antonio's most distinctive cultural phenomena. Today, Fiesta medals are produced 
              by hundreds of organizations, businesses, and individuals, each adding their own creative expression 
              to the tradition Coppini began.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              The clinking of medals on colorful sashes has become the soundtrack of Fiesta San Antonio. 
              Collectors eagerly trade and hunt for rare designs, while newcomers quickly learn that wearing 
              a medal-laden sash is an essential part of participating in the city's grandest celebration.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              Coppini's vision of celebrating San Antonio's unique character through civic engagement and 
              artistic expression lives on every April, when millions of medals change hands and the city 
              comes together in the spirit of the original Spring Carnival he helped shape.
            </p>
          </div>
        </div>
      </section>

      {/* Replica Program with Purchase */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">Preserving the Legacy: The Replica Medal Program</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Medal Image */}
            <div className="flex justify-center">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/ikff0it3_fiestamedal.png"
                  alt="Replica of the 1905 Fiesta Medal"
                  className="w-80 h-auto rounded-lg shadow-2xl"
                />
                <div className="absolute -top-3 -right-3 bg-cafa-red text-white px-4 py-2 rounded-full font-bold shadow-lg">
                  $25
                </div>
              </div>
            </div>
            
            {/* Purchase Info */}
            <div className="space-y-6">
              <div className="prose prose-lg prose-stone">
                <p className="text-lg text-stone-700 leading-relaxed">
                  In honor of this remarkable piece of San Antonio history, the Coppini Academy of Fine Arts 
                  produces faithful replicas of the original 1905 Fiesta medal. These copper-toned reproductions 
                  capture every detail of Coppini's original design.
                </p>
                <p className="text-lg text-stone-700 leading-relaxed">
                  Proceeds from replica medal sales directly support the Academy's educational mission, including 
                  the Coppini Kids program, which provides free art instruction to children at local shelters.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg border border-stone-200">
                <h3 className="text-xl font-semibold text-stone-900 mb-4">Purchase Your Replica Medal</h3>
                <ul className="space-y-2 text-stone-600 mb-6">
                  <li className="flex items-center gap-2">
                    <Award size={18} className="text-cafa-red" />
                    <span>Faithful reproduction of the 1905 original</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <MapPin size={18} className="text-cafa-red" />
                    <span>Pickup only at the Coppini Academy</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart size={18} className="text-cafa-red" />
                    <span>Supports youth art education</span>
                  </li>
                </ul>
                <Button 
                  onClick={() => setShowPurchaseModal(true)}
                  className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                  size="lg"
                >
                  <ShoppingCart size={20} className="mr-2" />
                  Buy Now — $25
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">A Living Tradition</h2>
          
          <div className="prose prose-lg prose-stone max-w-none">
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              The Coppini Academy of Fine Arts stands as the steward of Pompeo Coppini's remarkable legacy. 
              Operating from the very studio where he created some of his greatest works, the Academy preserves 
              not only his art and archives but also his vision of San Antonio as a city that celebrates its 
              heritage through artistic expression and civic engagement.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              We invite you to visit the Academy, explore our museum, and experience firsthand the history 
              that connects modern Fiesta to its origins over a century ago. Whether you're a lifelong 
              San Antonian or a first-time visitor, the story of Pompeo Coppini and the first Fiesta medal 
              offers a deeper appreciation for one of America's great civic celebrations.
            </p>
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="py-16 bg-stone-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-white mb-8 text-center">Support the Legacy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-cafa-red/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-cafa-red-100" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Purchase Replica Medal</h3>
              <p className="text-stone-300 text-sm mb-4">
                Own a piece of Fiesta history with a faithful reproduction of the 1905 original.
              </p>
              <Button asChild className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/contact">Inquire About Medals</Link>
              </Button>
            </div>
            
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-cafa-red/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-cafa-red-100" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Visit the Academy</h3>
              <p className="text-stone-300 text-sm mb-4">
                Explore Coppini's historic studio and view the original medal in our museum.
              </p>
              <Button asChild className="w-full bg-white text-stone-900 hover:bg-stone-100">
                <Link to="/contact">Plan Your Visit</Link>
              </Button>
            </div>
            
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-cafa-red/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-cafa-red-100" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Support Coppini Kids</h3>
              <p className="text-stone-300 text-sm mb-4">
                Help bring free art education to children in our community.
              </p>
              <Button asChild className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/support">Donate Today</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-serif text-stone-900">Purchase Replica Medal</h3>
                <button 
                  onClick={() => setShowPurchaseModal(false)}
                  className="text-stone-400 hover:text-stone-600"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="flex items-center gap-4 mb-6 p-4 bg-stone-50 rounded-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/ikff0it3_fiestamedal.png"
                  alt="Fiesta Medal Replica"
                  className="w-20 h-20 object-contain"
                />
                <div>
                  <p className="font-semibold text-stone-900">1905 Fiesta Medal Replica</p>
                  <p className="text-2xl font-bold text-cafa-red">$25.00</p>
                  <p className="text-sm text-stone-500">Pickup only at Coppini Academy</p>
                </div>
              </div>
              
              <form onSubmit={handlePurchaseSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={purchaseForm.name}
                    onChange={(e) => setPurchaseForm({...purchaseForm, name: e.target.value})}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-cafa-red focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={purchaseForm.email}
                    onChange={(e) => setPurchaseForm({...purchaseForm, email: e.target.value})}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-cafa-red focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={purchaseForm.phone}
                    onChange={(e) => setPurchaseForm({...purchaseForm, phone: e.target.value})}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-cafa-red focus:border-transparent"
                    placeholder="(210) 555-1234"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Quantity</label>
                  <select
                    value={purchaseForm.quantity}
                    onChange={(e) => setPurchaseForm({...purchaseForm, quantity: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-cafa-red focus:border-transparent"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n} medal{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Preferred Pickup Date *</label>
                  <input
                    type="date"
                    required
                    value={purchaseForm.pickupDate}
                    onChange={(e) => setPurchaseForm({...purchaseForm, pickupDate: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-cafa-red focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Preferred Pickup Time</label>
                  <select
                    value={purchaseForm.pickupTime}
                    onChange={(e) => setPurchaseForm({...purchaseForm, pickupTime: e.target.value})}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-cafa-red focus:border-transparent"
                  >
                    <option value="morning">Morning (9am - 12pm)</option>
                    <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  </select>
                </div>
                
                <div className="pt-4 border-t border-stone-200">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-medium text-stone-700">Total:</span>
                    <span className="text-2xl font-bold text-cafa-red">${purchaseForm.quantity * 25}.00</span>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                    size="lg"
                  >
                    {submitting ? 'Processing...' : 'Continue to PayPal'}
                  </Button>
                  
                  <p className="text-xs text-stone-500 text-center mt-3">
                    You'll be redirected to PayPal to complete your purchase securely.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiestaMedal;
